import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ControlBarButton } from '@m12s/component-library';
import { useMutation } from '@apollo/client';
import { signOut } from 'lib/icons';
import { FAISubmitWorkPromptModal } from 'components/FirstArticleInspection/FAISubmitWorkPromptModal';
import { REQUEST_FAI } from 'lib/api/mutations';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
import useStopOperatorRun from 'components/LaborTicket/hooks/useStopOperatorRun';
import {
  actionSetFAIStatus,
  actionOpenLaborTicketModal,
  actionOpenSetupActionModal,
} from 'lib/actions';
import { FAI_STATUS, LABOR_TICKET_ACTIONS } from 'lib/constants';
import { getCanRequestFAI } from 'lib/selectors/getCanRequestFAI';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getFAICurrentInspection } from 'lib/selectors/getFAICurrentInspection';
import { getFAILoading } from 'lib/selectors/getFAILoading';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getSkipSetupParts } from 'lib/selectors/getSkipSetupParts';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getHasClosedKeepOpenLaborTicket } from 'lib/selectors/getHasClosedKeepOpenLaborTicket';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import { getMachine } from 'lib/selectors/getMachine';
import Routes from 'lib/utils/routes';
import { MobileIcon, ResponsiveText } from '../styled';

/**
 * Renders the sign-out control-bar action with first-article inspection gating.
 *
 * When FAI is eligible (and no inspection is already active), clicking sign out
 * first prompts the operator to send a piece for inspection or skip. Skipping
 * proceeds with the normal sign-out flow. Otherwise sign-out fires immediately.
 */
const SignOutSection = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const machine = useSelector(getMachine);
  const isPaused = useSelector(getIsPaused);
  const skipSetupParts = useSelector(getSkipSetupParts);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  const isSetupTicket = currentLaborTicket?.type?.toLowerCase() === 'setup';
  const useSetupActionModal = skipSetupParts && isSetupTicket;
  const hasClosedKeepOpenLaborTicket = useSelector(
    getHasClosedKeepOpenLaborTicket
  );
  const requestOperatorLogout = useOperatorLogout();
  const requestStopOperatorRun = useStopOperatorRun();

  const canRequestFAI = useSelector(getCanRequestFAI);
  const currentInspection = useSelector(getFAICurrentInspection);
  const isInspectionActive =
    currentInspection?.status === FAI_STATUS.PENDING ||
    currentInspection?.status === FAI_STATUS.IN_PROGRESS;
  const faiLoading = useSelector(getFAILoading);
  const operator = useSelector(getCurrentOperator);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const machineRef = machine?.machineRef;
  const workOrderOperationRef = latestActivitySet?.workOrderOperationRef;
  const operatorId = operator?.id;

  const [requestFAI, { loading: requestFAILoading }] = useMutation(REQUEST_FAI);
  const [isFAIPromptOpen, setIsFAIPromptOpen] = useState(false);

  const handleSignOut = useCallback(() => {
    // Keep-open state: the labor ticket is already closed and the run is left
    // open for the next operator. There is no ticket to reconcile, so sign out
    // by ending only the operator run (routed through submit/submit-nats) rather
    // than opening the labor ticket form.
    if (hasClosedKeepOpenLaborTicket) {
      return requestStopOperatorRun();
    }
    if (isPaused) {
      return requestOperatorLogout();
    }
    history.push(Routes.machineIdHomePath(machine.id));
    if (useSetupActionModal) {
      return dispatch(actionOpenSetupActionModal('signout'));
    }
    return dispatch(
      actionOpenLaborTicketModal(
        LABOR_TICKET_ACTIONS.STOP_AND_CREATE_OPERATOR_LOGOUT
      )
    );
  }, [
    hasClosedKeepOpenLaborTicket,
    requestStopOperatorRun,
    isPaused,
    requestOperatorLogout,
    history,
    machine,
    useSetupActionModal,
    dispatch,
  ]);

  const handleSendForInspection = useCallback(() => {
    return requestFAI({
      variables: {
        input: { machineRef, workOrderOperationRef, operatorId },
      },
    }).then(({ data }) => {
      const res = data?.requestFAIResponse;
      if (res) {
        dispatch(
          actionSetFAIStatus({
            currentInspection: {
              inspectionRef: res.inspectionRef,
              machineRef: res.machineRef,
              workOrderOperationRef: res.workOrderOperationRef,
              status: res.status,
              operatorId: res.operatorId,
              requestedAt: res.requestedAt,
            },
          })
        );
      }
      setIsFAIPromptOpen(false);
    });
  }, [requestFAI, machineRef, workOrderOperationRef, operatorId, dispatch]);

  const handleSignOutClick = useCallback(() => {
    if (faiLoading) return null;

    // No open ticket in the keep-open state — skip the FAI prompt and sign out.
    if (!hasClosedKeepOpenLaborTicket && canRequestFAI && !isInspectionActive) {
      return setIsFAIPromptOpen(true);
    }
    return handleSignOut();
  }, [
    faiLoading,
    hasClosedKeepOpenLaborTicket,
    canRequestFAI,
    isInspectionActive,
    handleSignOut,
  ]);

  return (
    <>
      <FAISubmitWorkPromptModal
        isOpen={isFAIPromptOpen}
        onClose={() => {
          return setIsFAIPromptOpen(false);
        }}
        onSkip={() => {
          setIsFAIPromptOpen(false);
          handleSignOut();
        }}
        onSendForInspection={handleSendForInspection}
        sendLoading={requestFAILoading}
      />

      <ControlBarButton id="signout" onClick={handleSignOutClick}>
        <MobileIcon icon={signOut} />
        <ResponsiveText
          full={t('signout|full')}
          medium={t('signout|medium')}
          short={t('signout|short')}
        />
      </ControlBarButton>
    </>
  );
};

export { SignOutSection };
